export interface ErrorLineDetail {
  label: string;
  value: string;
}

export interface ErrorLineLink {
  label: string;
  to: string;
}

export interface ErrorLine {
  primary: string;
  secondary?: string;
  details?: ErrorLineDetail[];
  links?: ErrorLineLink[];
  variant?: "error" | "conflict";
}

export type HelpLinksByField = Record<string, ErrorLineLink[]>;

type Translate = (key: string, options?: Record<string, unknown>) => string;

const VALIDATION_ERROR_TRANSLATION_KEYS: Record<string, string> = {
  company_mandatory_anchor_data_missing: "draft_mode_missing_fields",
};

const isPrimitive = (value: unknown): value is string | number | boolean =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isHttpStatusLike = (key: string, value: unknown): boolean =>
  key === "status" &&
  (typeof value === "number" ||
    (typeof value === "string" && /^\d{3}$/.test(value.trim())));

// Parses strings that contain JSON objects/arrays; returns undefined otherwise. 


const parseEmbeddedJson = (value: string): unknown => {
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === "object" && parsed !== null ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const isEmptyStructure = (value: unknown): boolean =>
  (Array.isArray(value) && value.length === 0) ||
  (isPlainObject(value) && Object.keys(value).length === 0);

const looksLikeHtml = (value: string): boolean =>
  /<(?:!doctype\s+html|html|head|body|title|h[1-6]|center|p|pre)(?:\s|>)/i.test(
    value,
  );

const collapseWhitespace = (value: string): string =>
  value.replace(/\s+/g, " ").trim();

const getHtmlErrorText = (value: string): string | undefined => {
  if (!looksLikeHtml(value)) {
    return undefined;
  }

  if (typeof DOMParser !== "undefined") {
    const document = new DOMParser().parseFromString(value, "text/html");
    const title = collapseWhitespace(document.title);
    if (title) {
      return title;
    }

    document
      .querySelectorAll("script, style, noscript")
      .forEach((element) => element.remove());
    const bodyText = collapseWhitespace(document.body.textContent ?? "");
    return bodyText || undefined;
  }

  const titleMatch = value.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  const text = (titleMatch?.[1] ?? value)
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ");
  return collapseWhitespace(text) || undefined;
};

// ---unknown--
const collectLeafLines = (
  value: unknown,
  path: string,
  lines: ErrorLine[],
  foundStatus: { value: unknown },
): void => {
  if (typeof value === "string") {
    const embedded = parseEmbeddedJson(value);
    if (embedded !== undefined) {
      if (!isEmptyStructure(embedded)) {
        collectLeafLines(embedded, path, lines, foundStatus);
      }
      return;
    }
    lines.push({
      primary: getHtmlErrorText(value) ?? value,
      secondary: path || undefined,
    });
    return;
  }

  if (isPrimitive(value)) {
    lines.push({ primary: String(value), secondary: path || undefined });
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      collectLeafLines(item, `${path}[${index}]`, lines, foundStatus);
    });
    return;
  }

  if (isPlainObject(value)) {
    Object.entries(value).forEach(([key, entryValue]) => {
      if (isHttpStatusLike(key, entryValue) && foundStatus.value === undefined) {
        foundStatus.value = entryValue;
        return;
      }
      collectLeafLines(
        entryValue,
        path ? `${path}.${key}` : key,
        lines,
        foundStatus,
      );
    });
  }
  // null / undefined / functions: nothing renderable
};

const cleanConflictDetail = (detail: string): string =>
  detail.replace(/Use force=true to continue\/override\.*\s*$/i, "").trim();

const toCamelCase = (entity: string) =>
  entity.charAt(0).toLowerCase() + entity.slice(1);

const removeUnderscore = (value: string) => value.replace(/_/g, " ");

const isTranslationConflictEntry = (value: unknown): boolean =>
  isPlainObject(value) &&
  "translated_text" in value &&
  "detail" in value &&
  Array.isArray(value.objects);

const buildConflictLine = (entry: Record<string, any>): ErrorLine => ({
  variant: "conflict",
  primary: String(entry.translated_text),
  secondary: cleanConflictDetail(String(entry.detail)),
  links: entry.objects.map((obj: any) => ({
    label: removeUnderscore(String(obj.name)),
    to: `/${toCamelCase(String(obj.type))}/${obj.id}/translation/`,
  })),
});

//  Result objects like { message, form_names, ...extras } from batch endpoints. 


const buildResultObjectLine = (item: Record<string, any>, t: Translate): ErrorLine => {
  const details: ErrorLineDetail[] = [];
  if (Array.isArray(item.form_names)) {
    details.push({ label: t("form"), value: item.form_names.join(", ") });
  }
  Object.entries(item).forEach(([key, value]) => {
    if (key === "id" || key === "message" || key === "form_names") {
      return;
    }
    details.push({
      label: `${key}:`,
      value: Array.isArray(value) ? value.join(", ") : String(value),
    });
  });
  return {
    primary: item.message || item.id || JSON.stringify(item),
    details,
  };
};

const buildStructuredValidationLines = (
  value: Record<string, any>,
  t: Translate,
): ErrorLine[] => {
  const lines: ErrorLine[] = [
    {
      primary: t(
        VALIDATION_ERROR_TRANSLATION_KEYS[value.code] ?? "error_occurred",
      ),
      links: Array.isArray(value.links) ? value.links : undefined,
    },
  ];

  if (Array.isArray(value.fields)) {
    lines.push(
      ...value.fields.map((field: unknown) => ({
        primary: String(field),
        secondary: t("mandatory_field"),
      })),
    );
  }

  return lines;
};

const normalizeObjectEntry = (
  fieldName: string,
  fieldValue: unknown,
  t: Translate,
  deep: { lines: ErrorLine[]; foundStatus: { value: unknown } },
): ErrorLine[] => {
  if (
    isPlainObject(fieldValue) &&
    typeof fieldValue.code === "string" &&
    Array.isArray(fieldValue.fields)
  ) {
    return buildStructuredValidationLines(fieldValue, t);
  }

  if (isPrimitive(fieldValue)) {
    return [{ primary: String(fieldValue), secondary: fieldName }];
  }

  // { field: { value: [...] } } wrapper
  if (
    isPlainObject(fieldValue) &&
    Array.isArray((fieldValue as any).value)
  ) {
    const values = (fieldValue as any).value as any[];
    if (values.length > 0 && values.every((v) => isPlainObject(v))) {
      return values.flatMap((nested: Record<string, any>) =>
        Object.entries(nested).map(([key, val]) => ({
          primary: String(val),
          secondary: `${fieldName}.${key}`,
        })),
      );
    }
    if (values.every((v) => !isPlainObject(v) && !Array.isArray(v))) {
      return [{ primary: values.join(", "), secondary: fieldName }];
    }
    // mixed content: fall through to the generic walk below
  }

  if (Array.isArray(fieldValue)) {
    if (
      fieldValue.length > 0 &&
      fieldValue.every((item) => isPlainObject(item))
    ) {
      return fieldValue.map((item) => buildResultObjectLine(item, t));
    }
    if (fieldValue.every((item) => isPrimitive(item))) {
      return [{ primary: fieldValue.join(", "), secondary: fieldName }];
    }
  }

  // unknown nested structure: labeled leaf lines, statuses collected
  collectLeafLines(fieldValue, fieldName, deep.lines, deep.foundStatus);
  return [];
};

const attachHelpLinks = (
  lines: ErrorLine[],
  helpLinks: ErrorLineLink[] | undefined,
): ErrorLine[] =>
  helpLinks
    ? lines.map((line) => ({
        ...line,
        links: [...(line.links ?? []), ...helpLinks],
      }))
    : lines;

// ------
export const normalizeApiErrorToLines = (
  error: unknown,
  t: Translate,
  status?: unknown,
  helpLinksByField?: HelpLinksByField,
): ErrorLine[] => {
  if (typeof error === "function") {
    // side-effect errors render nothing; the modal invokes them separately
    return [];
  }

  if (typeof error === "string") {
    return [{ primary: getHtmlErrorText(error) ?? error }];
  }

  if (typeof error === "number" || typeof error === "boolean") {
    return [{ primary: String(error) }];
  }

  if (Array.isArray(error)) {
    const lines: ErrorLine[] = [];
    const foundStatus = { value: undefined as unknown };
    error.forEach((item, index) => {
      if (isPrimitive(item)) {
        lines.push({ primary: String(item) });
      } else {
        collectLeafLines(item, `[${index}]`, lines, foundStatus);
      }
    });
    return lines;
  }

  if (isPlainObject(error)) {
    if (String(status) === "409") {
      const conflictLines = Object.values(error)
        .filter(isTranslationConflictEntry)
        .map((entry) => buildConflictLine(entry as Record<string, any>));
      const resultLines = Object.values(error)
        .filter(
          (value) =>
            Array.isArray(value) &&
            value.length > 0 &&
            value.every((item) => isPlainObject(item)),
        )
        .flatMap((value: any) =>
          value.map((item: Record<string, any>) =>
            buildResultObjectLine(item, t),
          ),
        );
      const lines = [...conflictLines, ...resultLines];
      if (lines.length > 0) {
        return lines;
      }
    }

    const deep = {
      lines: [] as ErrorLine[],
      foundStatus: { value: undefined as unknown },
    };
    const recognizedLines = Object.entries(error).flatMap(
      ([fieldName, fieldValue]) =>
        attachHelpLinks(
          normalizeObjectEntry(fieldName, fieldValue, t, deep),
          helpLinksByField?.[fieldName],
        ),
    );

    const lines: ErrorLine[] = [];
    if (deep.foundStatus.value !== undefined) {
      lines.push({
        primary: t("generic_error_message", { error: deep.foundStatus.value }),
      });
    }
    lines.push(...recognizedLines, ...deep.lines);

    if (lines.length === 0) {
      return [{ primary: t("error_occurred") }];
    }
    return lines;
  }

  // null, undefined, symbols, ...
  return [{ primary: t("error_occurred") }];
};
